import { useMemo } from "react";

import { SegmentedMessage } from "sms-segments-calculator";
import {
  FeatureFlagReturnType,
  useFlags,
} from "src/components/Providers/FeatureFlagProvider";
import { $isVariable } from "src/components/WysiwygEditor/lexical/hooks/useVariables";

const GSM_CHAR_PER_SEGMENT = 153;
const UCS2_CHAR_PER_SEGMENT = 67;

// Guardrails so a malformed flag value can never break the editor.
const MIN_SEGMENT_MAX = 1;
const MAX_SEGMENT_MAX = 5;

type SegmentLimits = {
  gsmSegmentMax: number;
  ucs2SegmentMax: number;
};

const DEFAULT_SEGMENT_LIMITS: SegmentLimits = {
  gsmSegmentMax: 2,
  ucs2SegmentMax: 2,
};

function clampSegmentMax(value: unknown, fallback: number): number {
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) {
    return fallback;
  }
  return Math.min(Math.max(Math.trunc(num), MIN_SEGMENT_MAX), MAX_SEGMENT_MAX);
}

/**
 * Reads the per-org segment limits from the `segmented_message_warning`
 * Flagsmith flag. The flag value is expected to be a JSON object such as
 * `{ "gsm_segment_max": 2, "ucs2_segment_max": 2 }`. Falls back to
 * {@link DEFAULT_SEGMENT_LIMITS} when the flag is disabled, empty, or malformed.
 */
function parseSegmentLimits(
  segmentLimitsFeatureFlag: FeatureFlagReturnType
): SegmentLimits {
  const flag = segmentLimitsFeatureFlag["segmented_message_warning"];
  if (!flag?.enabled || flag.value == null) {
    return DEFAULT_SEGMENT_LIMITS;
  }

  try {
    const parsed =
      typeof flag.value === "string" ? JSON.parse(flag.value) : flag.value;
    if (typeof parsed !== "object" || parsed === null) {
      return DEFAULT_SEGMENT_LIMITS;
    }
    return {
      gsmSegmentMax: clampSegmentMax(
        parsed.gsm_segment_max,
        DEFAULT_SEGMENT_LIMITS.gsmSegmentMax
      ),
      ucs2SegmentMax: clampSegmentMax(
        parsed.ucs2_segment_max,
        DEFAULT_SEGMENT_LIMITS.ucs2SegmentMax
      ),
    };
  } catch {
    return DEFAULT_SEGMENT_LIMITS;
  }
}

export class AvelaSegmentedMessage extends SegmentedMessage {
  hasVariables: boolean;
  limits: SegmentLimits;

  constructor(text: string, limits: SegmentLimits = DEFAULT_SEGMENT_LIMITS) {
    super(text);
    this.hasVariables = $isVariable(text);
    this.limits = limits;
  }

  isGSM(): boolean {
    return !this.hasVariables && this.encodingName === "GSM-7";
  }

  isUCS2(): boolean {
    return this.hasVariables || this.encodingName === "UCS-2";
  }

  segmentLimit(): number {
    return this.isUCS2()
      ? this.limits.ucs2SegmentMax
      : this.limits.gsmSegmentMax;
  }

  characterLimit(): number {
    return this.isUCS2()
      ? UCS2_CHAR_PER_SEGMENT * this.limits.ucs2SegmentMax
      : GSM_CHAR_PER_SEGMENT * this.limits.gsmSegmentMax;
  }

  isOverflowing(): boolean {
    return this.numberOfCharacters > this.characterLimit();
  }
}

export function toSegmentedMessage(
  text: string,
  segmentLimitsFeatureFlag: FeatureFlagReturnType
): AvelaSegmentedMessage {
  return new AvelaSegmentedMessage(
    text,
    parseSegmentLimits(segmentLimitsFeatureFlag)
  );
}

type Props = {
  text: string;
};

export function useSegmentedMessage({ text }: Props) {
  const segmentLimitsFeatureFlag = useFlags(["segmented_message_warning"]);
  const flag = segmentLimitsFeatureFlag["segmented_message_warning"];
  const enabled = flag?.enabled;
  const value = flag?.value;

  const segmentedMessage = useMemo(
    () => toSegmentedMessage(text, segmentLimitsFeatureFlag),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [text, enabled, value]
  );

  return { segmentedMessage };
}
